import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Clock, Calendar, Users, ArrowRight } from 'lucide-react';

const NDECC = () => {
  const examComponents = [
    "Clinical Skills Assessment",
    "OSCE Stations",
    "Restorative Procedures",
    "Endodontic Treatment",
    "Periodontal Procedures",
    "Prosthodontic Tasks",
    "Oral Surgery Skills",
    "Patient Management"
  ];

  const trainingIncludes = [
    "Comprehensive clinical skills training",
    "OSCE station practice and simulation",
    "Real exam environment replication",
    "Multiple mock examinations",
    "Rubric-based assessment and feedback",
    "Time management strategies",
    "Equipment and materials provided",
    "Personalized mentoring and guidance"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              NDECC Preparation Course
            </h1>
            <p className="text-lg md:text-xl text-gray-100 mb-8">
              Comprehensive training for the National Dental Examining Committee of Canada (NDECC) certification examination
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
                <Link to="/contact">Enroll Now</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/courses">View All Courses</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <Clock className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">5 Weeks</div>
                <div className="text-sm text-gray-600">Duration</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Mon-Fri</div>
                <div className="text-sm text-gray-600">10 AM - 7 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Saturday</div>
                <div className="text-sm text-gray-600">10 AM - 2 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Users className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Full-time</div>
                <div className="text-sm text-gray-600">Hands-on Training</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About NDECC */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                About NDECC Examination
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                The National Dental Examining Committee of Canada (NDECC) administers the examination required for dental licensure in Canada. This comprehensive assessment evaluates both clinical skills and theoretical knowledge.
              </p>
              <p className="text-gray-600 mb-6">
                Our training program is specifically designed to prepare you for the rigorous demands of the NDECC examination, focusing on both technical proficiency and patient management skills.
              </p>
              <Button asChild className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                <Link to="/contact">Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="bg-[#1e3a8a]/5 rounded-2xl p-8 border-2 border-[#1e3a8a]/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Exam Components</h3>
              <div className="grid grid-cols-2 gap-4">
                {examComponents.map((component, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{component}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Includes */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Training Includes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive preparation designed to give you confidence and competence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingIncludes.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Methodology */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Training Methodology
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">01</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Assessment-Based Learning
              </h3>
              <p className="text-gray-600">
                Initial assessment to identify strengths and areas for improvement, followed by targeted training
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">02</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Hands-On Practice
              </h3>
              <p className="text-gray-600">
                Intensive daily practice sessions with real equipment and materials under expert supervision
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">03</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Mock Examinations
              </h3>
              <p className="text-gray-600">
                Regular mock exams to build confidence and refine examination strategies
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Begin Your NDECC Preparation Journey
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your goals and start your path to Canadian dental licensure
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
              <Link to="/contact">Enroll Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/courses">View All Courses</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NDECC;
